/**
 * Mammography view label, the way a reader names a film: laterality + view + modifiers, e.g. "LCC",
 * "RMLO", "LCC MAG SPOT". Shown bold at the top of the top-left block of every MG image, from that
 * image's own header (so four cells of a 4-view study read LCC / RCC / LMLO / RMLO).
 *
 * - Laterality: Image Laterality (0020,0062), else the series Laterality (0020,0060); R or L only.
 * - View: View Code Sequence (0054,0220), CID 4014. Its Code Meaning is matched first (the text is
 *   fixed by the standard and survives the SRT → SNOMED CT code change), then its Code Value
 *   (current SCT and legacy SRT), then View Position (0018,5101) as written by older modalities
 *   ("CC", "MLO", also "LCC" with the side in it).
 * - Modifiers: View Modifier Code Sequence (0054,0222), CID 4015, nested in the view item or at
 *   the top level.
 * No label when laterality or view cannot be resolved (e.g. tomosynthesis without view codes).
 */

/** What the label needs from a header: attribute text, and the items of a sequence. */
export interface CodedTags {
  text(tag: string): string;
  items(tag: string): CodedTags[];
}

const TAG = {
  imageLaterality: '00200062',
  laterality: '00200060',
  viewPosition: '00185101',
  viewCode: '00540220',
  viewModifier: '00540222',
  codeValue: '00080100',
  codeMeaning: '00080104',
} as const;

export const MAMMO_VIEWS = ['CC', 'MLO', 'ML', 'LM', 'LMO', 'FB', 'XCC', 'XCCL', 'XCCM', 'SIO', 'ISO'] as const;
export type MammoView = (typeof MAMMO_VIEWS)[number];

/** Code Meaning (normalised: lower case, no hyphens) → view; the more specific phrases first. */
const VIEW_MEANINGS: Array<[RegExp, MammoView]> = [
  [/exaggerated craniocaudal.*lateral/, 'XCCL'],
  [/exaggerated craniocaudal.*medial/, 'XCCM'],
  [/exaggerated craniocaudal/, 'XCC'],
  [/superolateral to inferomedial/, 'SIO'],
  [/inferomedial to superolateral/, 'ISO'],
  [/caudocranial|from below/, 'FB'],
  [/craniocaudal/, 'CC'],
  [/mediolateral oblique/, 'MLO'],
  [/lateromedial oblique/, 'LMO'],
  [/mediolateral/, 'ML'],
  [/lateromedial/, 'LM'],
];

/** CID 4014 code values: SNOMED CT and the legacy SNOMED-RT (SRT) ones still in older headers. */
const VIEW_CODES: Record<string, MammoView> = {
  '399162004': 'CC', 'R-10242': 'CC',
  '399368009': 'MLO', 'R-10226': 'MLO',
  '399260004': 'ML', 'R-10224': 'ML',
  '399352003': 'LM', 'R-10228': 'LM',
  '399099002': 'LMO', 'R-10230': 'LMO',
  '399196006': 'FB', 'R-10244': 'FB',
  '399192008': 'XCCL', 'R-1024A': 'XCCL',
  '399101009': 'XCCM', 'R-1024B': 'XCCM',
  '399188001': 'SIO', 'R-10256': 'SIO',
};

/** CID 4015 modifiers by Code Meaning, as the abbreviations readers use. */
const MODIFIER_MEANINGS: Array<[RegExp, string]> = [
  [/magnif/, 'MAG'],
  [/spot compression/, 'SPOT'],
  [/implant displaced/, 'ID'],
  [/cleavage/, 'CV'],
  [/axillary tail/, 'AT'],
  [/rolled lateral/, 'RL'],
  [/rolled medial/, 'RM'],
  [/rolled inferior/, 'RI'],
  [/rolled superior/, 'RS'],
  [/tangential/, 'TAN'],
  [/nipple in profile/, 'NP'],
];

function normalise(text: string): string {
  return text.toLowerCase().replace(/[-_]/g, '').replace(/\s+/g, ' ').trim();
}

function viewOfItem(item: CodedTags): MammoView | undefined {
  const meaning = normalise(item.text(TAG.codeMeaning));
  if (meaning) {
    for (const [pattern, view] of VIEW_MEANINGS) if (pattern.test(meaning)) return view;
  }
  return VIEW_CODES[item.text(TAG.codeValue).trim().toUpperCase()] ?? VIEW_CODES[item.text(TAG.codeValue).trim()];
}

/** View Position as written by modalities: "CC", "MLO", "L CC", "R.MLO", "RCC"... */
export function viewFromPosition(position: string): MammoView | undefined {
  const compact = position.toUpperCase().replace(/[.\s_-]/g, '');
  if ((MAMMO_VIEWS as readonly string[]).includes(compact)) return compact as MammoView;
  const withoutSide = /^[RL]/.test(compact) ? compact.slice(1) : '';
  return (MAMMO_VIEWS as readonly string[]).includes(withoutSide) ? (withoutSide as MammoView) : undefined;
}

function lateralityOf(tags: CodedTags): 'R' | 'L' | undefined {
  for (const tag of [TAG.imageLaterality, TAG.laterality]) {
    const value = tags.text(tag).trim().toUpperCase();
    if (value === 'R' || value === 'L') return value;
  }
  // Last resort: the side written into View Position ("LCC", "R MLO").
  const position = tags.text(TAG.viewPosition).toUpperCase().replace(/[.\s_-]/g, '');
  const side = position[0];
  const rest = position.slice(1);
  if ((side === 'R' || side === 'L') && (MAMMO_VIEWS as readonly string[]).includes(rest)) return side;
  return undefined;
}

function modifiersOf(tags: CodedTags, viewItem: CodedTags | undefined): string[] {
  const items = [...(viewItem?.items(TAG.viewModifier) ?? []), ...tags.items(TAG.viewModifier)];
  const found: string[] = [];
  for (const item of items) {
    const meaning = normalise(item.text(TAG.codeMeaning));
    const match = MODIFIER_MEANINGS.find(([pattern]) => pattern.test(meaning));
    if (match && !found.includes(match[1])) found.push(match[1]);
  }
  return found;
}

/** "LCC", "RMLO MAG"…, or '' when the header does not say which breast and which view. */
export function mammoViewLabel(tags: CodedTags): string {
  const viewItem = tags.items(TAG.viewCode)[0];
  const view = (viewItem && viewOfItem(viewItem)) ?? viewFromPosition(tags.text(TAG.viewPosition));
  const side = lateralityOf(tags);
  if (!view || !side) return '';
  return [`${side}${view}`, ...modifiersOf(tags, viewItem)].join(' ');
}
